import { c as _c } from "react/compiler-runtime";
import { SiCloudflare } from "@icons-pack/react-simple-icons";
import { useSuspenseQuery } from "@tanstack/react-query";
import Link from "next/link";
import { useAnalytics } from "@/lib/analytics/client";
import { useTRPC } from "@/lib/trpc/client";
import { REGISTRAR_PROVIDERS } from "@domainstack/constants";
import type { RegistrarKey } from "@domainstack/types";
import { Button } from "@domainstack/ui/button";
import { Skeleton } from "@domainstack/ui/skeleton";
import { Tooltip, TooltipContent, TooltipTrigger } from "@domainstack/ui/tooltip";
import { cn } from "@domainstack/ui/utils";
import { formatPrice } from "@domainstack/utils/price";
const REGISTRAR_ICONS: Record<RegistrarKey, React.ReactNode> = {
  porkbun: <svg viewBox="0 0 24 24" fill="currentColor">
      <circle fill="white" cx="12" cy="12" r="12" />
      <path fill="#EF7878" d="M12 0A11.97 11.97 0 0 0 .018 11.982C.018 18.612 5.37 24 12 24s11.982-5.353 11.982-11.982C23.982 5.388 18.63 0 12 0M5.832 5.885c1.064.248 2.092.638 3.014 1.135-1.1.531-1.987 1.382-2.66 2.375a3.4 3.4 0 0 1-.674-2.057c0-.532.107-.992.32-1.453m12.336 0c.213.425.32.921.32 1.453 0 .78-.248 1.49-.674 2.057-.673-.993-1.596-1.844-2.66-2.375a10 10 0 0 1 3.014-1.135m-6.072.81a6.39 6.39 0 0 1 6.32 6.457v3.829a1.18 1.18 0 0 1-1.17 1.17 1.18 1.18 0 0 1-1.17-1.17v-.958H7.852v.957a1.18 1.18 0 0 1-1.17 1.17 1.18 1.18 0 0 1-1.17-1.17v-3.65c0-3.51 2.73-6.489 6.24-6.63q.173-.007.344-.005m1.5 3.8a.94.94 0 0 0-.922.921c0 .248.07.424.213.602.141.212.353.354.566.46-.142.071-.319.143-.496.143-.213 0-.39.176-.39.389s.177.39.39.39h.178a1.56 1.56 0 0 0 1.383-.851c.39-.142.709-.39.921-.744.071-.107.034-.249-.037-.213-.106-.036-.212-.034-.283.072a1.04 1.04 0 0 1-.426.354v-.143c0-.39-.14-.709-.353-.992a.88.88 0 0 0-.744-.389m0 .53c.212 0 .353.141.388.354v.178c0 .177-.034.354-.105.496a1.06 1.06 0 0 1-.604-.426c-.035-.071-.07-.14-.07-.211 0-.24.206-.39.39-.39" />
    </svg>,
  cloudflare: <SiCloudflare fill="#F38020" />,
  dynadot: <svg viewBox="0 0 24 24" fill="#1FA7FF">
      <path d="M19.688 5.66c-1.218-2.439-4.29-3.355-6.708-2.098L5.82 7.286l1.605 3.081-1.618.84c-2.395 1.245-3.412 4.262-2.15 6.649a4.88 4.88 0 0 0 6.552 2.044l7.258-3.776-1.6-3.078 1.71-.89a4.877 4.877 0 0 0 2.111-6.494zM9.376 18.293a3.07 3.07 0 0 1-4.419-2.107 2.7 2.7 0 0 1-.059-.757c.073-1.13.697-2.076 1.653-2.57l1.708-.89.707 1.36a2.936 2.936 0 0 0 3.955 1.25l1.343-.698.767 1.475-5.653 2.938zm2.542-7.961c.551.105.995.548 1.101 1.098a1.403 1.403 0 0 1-1.648 1.65 1.4 1.4 0 0 1-1.101-1.102 1.403 1.403 0 0 1 1.648-1.648zm4.75.256-1.635.852-.707-1.358a2.934 2.934 0 0 0-3.955-1.25l-1.344.699-.768-1.476 5.654-2.938a3.07 3.07 0 0 1 4.107 1.258c.814 1.506.167 3.424-1.353 4.215z" />
    </svg>
};
function isRegistrarKey(value: string): value is RegistrarKey {
  return Object.hasOwn(REGISTRAR_PROVIDERS, value);
}

/**
 * Skeleton for RegistrarLinks.
 * Exported for use as Suspense fallback in parent components.
 */
export function RegistrarLinksSkeleton(t0) {
  const $ = _c(8);
  const {
    className
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = Object.keys(REGISTRAR_PROVIDERS);
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const providerCount = t1.length;
  let t2;
  if ($[1] !== className) {
    t2 = cn("flex flex-col items-center justify-center", className);
    $[1] = className;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <Skeleton className="mb-2 h-[13px] w-20" aria-hidden={true} />;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  let t5;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <div className="flex flex-col gap-2">{Array.from({
        length: providerCount
      }, _temp)}</div>;
    t5 = <Skeleton className="mt-6 h-4 w-64" aria-hidden={true} />;
    $[4] = t4;
    $[5] = t5;
  } else {
    t4 = $[4];
    t5 = $[5];
  }
  let t6;
  if ($[6] !== t2) {
    t6 = <div className={t2}>{t3}{t4}{t5}</div>;
    $[6] = t2;
    $[7] = t6;
  } else {
    t6 = $[7];
  }
  return t6;
}

/**
 * Displays registrar pricing links for a given domain.
 * Parent must extract and validate TLD before rendering.
 * Uses Suspense for loading state - wrap with Suspense boundary.
 */
function _temp(_, i) {
  return <Skeleton key={i} className="h-9 w-full min-w-[250px] rounded-md" aria-hidden={true} />;
}
export function RegistrarLinks(t0) {
  const $ = _c(30);
  const {
    domain,
    tld,
    className
  } = t0;
  const trpc = useTRPC();
  const analytics = useAnalytics();
  let t1;
  if ($[0] !== tld || $[1] !== trpc.registrar.getPricing) {
    t1 = trpc.registrar.getPricing.queryOptions({
      tld
    });
    $[0] = tld;
    $[1] = trpc.registrar.getPricing;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== t1) {
    t2 = {
      ...t1,
      staleTime: Number.POSITIVE_INFINITY
    };
    $[3] = t1;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const {
    data
  } = useSuspenseQuery(t2);
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  if ($[5] !== analytics || $[6] !== className || $[7] !== data?.data?.providers || $[8] !== domain || $[9] !== tld) {
    t7 = Symbol.for("react.early_return_sentinel");
    bb0: {
      const providers = data?.data?.providers ?? [];
      if (providers.length === 0) {
        t7 = null;
        break bb0;
      }
      const sortedProviders = [...providers].sort(_temp2);
      if ($[15] !== className) {
        t5 = cn("flex flex-col items-center justify-center", className);
        $[15] = className;
        $[16] = t5;
      } else {
        t5 = $[16];
      }
      if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
        t6 = <p className="mb-2 text-[13px] text-muted-foreground">…until now?</p>;
        $[17] = t6;
      } else {
        t6 = $[17];
      }
      t3 = "flex flex-col gap-2";
      let t8;
      if ($[18] !== analytics || $[19] !== domain || $[20] !== tld) {
        t8 = providerPricing => {
          if (!isRegistrarKey(providerPricing.provider)) {
            return null;
          }
          const config = REGISTRAR_PROVIDERS[providerPricing.provider];
          const price = formatPrice(providerPricing.price);
          if (!price) {
            return null;
          }
          return <Button key={providerPricing.provider} variant="outline" className="flex w-full min-w-[250px] items-center gap-2.5 [&_svg]:!size-5 [&_svg]:!shrink-0" nativeButton={false} aria-label={`Register this domain with ${config.name}`} render={<a href={config.searchUrl(domain)} target="_blank" rel="noopener" onClick={() => analytics.track("registrar_referral_clicked", {
            domain,
            provider: providerPricing.provider
          })}><Tooltip><TooltipTrigger render={<span className="rounded-full">{REGISTRAR_ICONS[providerPricing.provider]}</span>} /><TooltipContent>{config.name}</TooltipContent></Tooltip><span><span className="text-foreground/85">.{tld} from</span>{" "}<span className="font-semibold" suppressHydrationWarning={true}>{price}</span><span className="text-xs text-muted-foreground">/year</span></span></a>} />;
        };
        $[18] = analytics;
        $[19] = domain;
        $[20] = tld;
        $[21] = t8;
      } else {
        t8 = $[21];
      }
      t4 = sortedProviders.map(t8);
    }
    $[5] = analytics;
    $[6] = className;
    $[7] = data?.data?.providers;
    $[8] = domain;
    $[9] = tld;
    $[10] = t3;
    $[11] = t4;
    $[12] = t5;
    $[13] = t6;
    $[14] = t7;
  } else {
    t3 = $[10];
    t4 = $[11];
    t5 = $[12];
    t6 = $[13];
    t7 = $[14];
  }
  if (t7 !== Symbol.for("react.early_return_sentinel")) {
    return t7;
  }
  let t8;
  if ($[22] !== t3 || $[23] !== t4) {
    t8 = <div className={t3}>{t4}</div>;
    $[22] = t3;
    $[23] = t4;
    $[24] = t8;
  } else {
    t8 = $[24];
  }
  let t9;
  if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = <p className="mt-6 text-xs text-muted-foreground">These are not affiliate links, but they{" "}<Link href="/help#contact" className="text-foreground/85 underline underline-offset-2 hover:text-foreground/60">might be</Link>{" "}in the future!</p>;
    $[25] = t9;
  } else {
    t9 = $[25];
  }
  let t10;
  if ($[26] !== t5 || $[27] !== t6 || $[28] !== t8) {
    t10 = <div className={t5}>{t6}{t8}{t9}</div>;
    $[26] = t5;
    $[27] = t6;
    $[28] = t8;
    $[29] = t10;
  } else {
    t10 = $[29];
  }
  return t10;
}
function _temp2(a, b) {
  const priceA = Number.parseFloat(a.price);
  const priceB = Number.parseFloat(b.price);
  return priceA - priceB;
}